/**
 * Every localStorage key that holds ONE PERSON'S data, in one place.
 *
 * The app paints from cache before the network answers - that is the whole
 * reason these keys exist, and it is worth keeping. But it means the FIRST
 * FRAME after a sign-in is drawn from whatever is on disk, and if that is the
 * previous account's data, the new user's first impression of their portfolio
 * is somebody else's projects.
 *
 * `performSignOut` used to clear the `rm-dash-*` keys and the resume draft, and
 * those two were found the way bugs like this are always found: somebody saw
 * their own name in a stranger's builder. The other five were written later, in
 * five different files, and none of them were added to the wipe:
 *
 *   - the portfolio cache - employers, titles, dates, projects, education;
 *   - the job-match cache - the roles ranked FOR that person, per region,
 *     with their saved and applied state;
 *   - the skills gap - what they are missing for the work they want;
 *   - the previous ATS score, used to render "+6 since last time";
 *   - the client-side achievement unlocks.
 *
 * A registry rather than a list of literals scattered across pages, because the
 * failure mode is not "the wipe was written wrong" - it is "somebody added a
 * sixth cache and nobody remembered this file existed". Read your key from
 * here and it is covered by construction; `userCache.test.ts` fails the build
 * if a `localStorage` key appears in `src/` that this registry does not know
 * about.
 *
 * NOT here: preferences that are about the BROWSER rather than the person
 * (theme, remember-me, whether a tour was dismissed). Wiping those on sign-out
 * would relight dismissed nudges and flip the theme back on a shared machine,
 * which is annoying rather than a leak.
 */

/** Exact keys. */
export const USER_CACHE_KEYS = [
  "rm-dash-insights",
  "rm-dash-activity",
  "rm-dash-sources",
  "rm-dash-connectors",
  "resumark:portfolio:v1",
  "rm_skills_gap_v1",
  "resumark-ats-previous",
  "resumark_client_achievements",
  "rm-profile-draft",
] as const;

/** Key families - one entry per region, per id, and so on. */
export const USER_CACHE_PREFIXES = ["rm-dash-", "resumark:jobs:"] as const;

/** The portfolio read-through cache (pages/portfolio.hooks.ts). */
export const PORTFOLIO_CACHE_KEY = "resumark:portfolio:v1";
/** Ranked job matches, one entry per region (pages/JobsPage.tsx). */
export const JOBS_CACHE_PREFIX = "resumark:jobs:";
/** The skills-gap analysis (pages/SkillsPage.tsx). */
export const SKILLS_CACHE_KEY = "rm_skills_gap_v1";
/** Last ATS score, for the "since last time" delta (stores/metrics.ts). */
export const ATS_PREVIOUS_KEY = "resumark-ats-previous";
/** Achievements unlocked client-side (stores/badges.ts). */
export const ACHIEVEMENTS_KEY = "resumark_client_achievements";
/**
 * Fingerprint of the last profile-derived draft (pages/ResumePage.tsx) - how
 * the page tells "the sync produced this" from "the person edited it". Left
 * behind, it also made the NEXT account look like they had edited a draft they
 * had never opened.
 */
export const PROFILE_MARK = "rm-profile-draft";

/**
 * Forget everything cached about the person who was signed in.
 *
 * Never throws: storage can be blocked (private mode, a policy) and a sign-out
 * that fails because it could not tidy up is worse than one that tidies up
 * partially.
 */
export function clearUserCaches(): void {
  try {
    for (const key of USER_CACHE_KEYS) localStorage.removeItem(key);
    // `length` + `key(i)`, NOT `Object.keys(localStorage)`. The index API is
    // the part of the Storage interface that is actually specified; the
    // property enumeration is a browser convenience that some Storage
    // implementations (including the one under our own test runner) answer
    // with the interface's METHOD names instead of the stored keys - so the
    // prefix sweep silently matched nothing and every keyed family survived
    // sign-out. Walked backwards because removing an entry renumbers the ones
    // after it.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && USER_CACHE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* storage blocked - nothing to clear */
  }
}
